import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { api } from "../api/api";
import Input from "./Input";
import Button from "./Button";
import CustomDropdown from "./CustomDropdown";
import ProviderSelector from "./ProviderSelector";
import { useApiCall } from "../hooks/useApiCall";
import Loader from "./Loader";

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ModalContent = styled.div`
  background-color: #0e1f1f;
  border-radius: 16px;
  padding: 18px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
  position: relative;

  /* Стилизация скроллбара */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 8px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #609f94;
    border-radius: 3px;
    border: 1px solid #0e1f1f;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #24ba7d;
  }

  /* Для Firefox */
  scrollbar-width: thin;
  scrollbar-color: #609f94 transparent;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #838d90;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #ffffff;
  }
`;

const SearchSection = styled.div`
  margin-bottom: 20px;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ContentContainer = styled.div`
  display: flex;
  gap: 24px;
  flex: 1;
`;

const FormSection = styled.div`
  flex: 1;
`;

const PreviewSection = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProductPreview = styled.div`
  background-color: #172a2a;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  text-align: center;
`;

const ProductImage = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 16px;
  object-fit: contain;
  background-color: #284640;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProductName = styled.h3`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #ffffff;
  margin: 0;
`;

const ProductColor = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #609f94;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  grid-column: 1 / -1;
  margin-top: 4px;
`;

const CheckboxWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Checkbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CheckboxCustom = styled.div<{ $checked: boolean }>`
  width: 20px;
  height: 20px;
  border: 1px solid
    ${(props) =>
      props.$checked ? "var(--color-primary)" : "rgba(255, 255, 255, 0.12)"};
  border-radius: 8px;
  background-color: ${(props) =>
    props.$checked ? "var(--color-primary)" : "rgba(255, 255, 255, 0.04)"};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) =>
      props.$checked ? "var(--color-primary)" : "rgba(255, 255, 255, 0.24)"};
  }

  &::after {
    content: "";
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    opacity: ${(props) => (props.$checked ? 1 : 0)};
    transition: opacity 0.2s ease;
  }
`;

const CheckboxLabel = styled.label`
  font-family: var(--font-family-main);
  font-weight: 400;
  font-size: 16px;
  color: var(--color-text-secondary);
  cursor: pointer;
  user-select: none;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin-bottom: 12px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(14, 31, 31, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  z-index: 100;
  backdrop-filter: blur(2px);
`;

interface ProductInfo {
  model: string;
  color: string;
  photo_url: string;
  brand?: string;
}

interface Brand {
  id: number;
  name: string;
}

interface Condition {
  id: number;
  name: string;
}

interface Provider {
  name: string;
}

import type { IMetroStation } from "../api/api";

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: any) => Promise<void>;
}

// Заранее заготовленные размеры
const US_SIZES = Array.from({ length: 30 }, (_, i) => 3.5 + i * 0.5); // 3.5 - 18
const UK_SIZES = Array.from({ length: 29 }, (_, i) => 3 + i * 0.5); // 3 - 17
const EU_SIZES = Array.from({ length: 36 }, (_, i) => 35 + i * 0.5); // 35 - 52

// Опции для dropdown'ов
const US_SIZE_OPTIONS = US_SIZES.map((size) => ({
  value: size.toString(),
  label: size.toString(),
}));
const UK_SIZE_OPTIONS = UK_SIZES.map((size) => ({
  value: size.toString(),
  label: size.toString(),
}));
const EU_SIZE_OPTIONS = EU_SIZES.map((size) => ({
  value: size.toString(),
  label: size.toString(),
}));

const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onAdd,
}) => {
  const { executeApiCall } = useApiCall();
  const [article, setArticle] = useState("");
  const [productInfo, setProductInfo] = useState<ProductInfo | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [brands, setBrands] = useState<Brand[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [metroStations, setMetroStations] = useState<string[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [formData, setFormData] = useState({
    brand: "",
    usSize: "",
    ukSize: "",
    euSize: "",
    price: "",
    cost: "",
    condition: "",
    provider: "",
    city: "",
    place: "",
    fitting: false,
    purchaseDate: new Date().toISOString().split("T")[0],
  });

  // Load brands and conditions on modal open
  useEffect(() => {
    const loadData = async () => {
      if (!isOpen) return;

      setIsLoadingData(true);
      try {
        const [brandsResponse, conditionsResponse, providersResponse, citiesResponse, metroResponse] =
          await Promise.all([
            api.getBrands(),
            api.getConditions(),
            api.getProviders(),
            api.getAvailableCities(),
            api.getMetroStations(),
          ]);

        setBrands(brandsResponse.brands || []);
        setConditions(conditionsResponse.conditions || []);
        setProviders(providersResponse || []);
        setCities(citiesResponse.cities || []);
        setMetroStations(metroResponse.stations || []);

        // Set default values only if no product info loaded yet
        if (brandsResponse.brands?.length > 0 && !formData.brand && !productInfo) {
          setFormData((prev) => ({
            ...prev,
            brand: brandsResponse.brands[0].name,
          }));
        }
        if (conditionsResponse.conditions?.length > 0 && !formData.condition) {
          setFormData((prev) => ({
            ...prev,
            condition: conditionsResponse.conditions[0].name,
          }));
        }
      } catch (error) {
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [isOpen, productInfo?.brand]); // Перезагружаем если изменился бренд в productInfo

  // Управление скроллом body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Debounced search effect
  useEffect(() => {
    const searchProduct = async () => {
      if (!article.trim()) {
        setProductInfo(null);
        setValidationError("");
        return;
      }

      setIsSearching(true);
      setValidationError("");

      const result = await executeApiCall(
        () => api.loadProductInfo(article.trim()),
        {
          showSuccessToast: false,
          showErrorToast: false,
        }
      );

      if (result && result.model?.trim()) {
        const color = result.color?.trim() ? result.color : "NA";
        const productInfo = { ...result, color };
        setProductInfo(productInfo);
        
        // Автоматически устанавливаем бренд из API (приоритет над значением по умолчанию)
        if (result.brand?.trim()) {
          const apiBrand = result.brand.trim();
          
          // Ищем точное совпадение
          let matchedBrand = brands.find(b => b.name === apiBrand);
          
          // Если точного совпадения нет, ищем по регистронезависимому сравнению
          if (!matchedBrand) {
            matchedBrand = brands.find(b => 
              b.name.toLowerCase() === apiBrand.toLowerCase()
            );
          }
          
          // Если не нашли, добавляем API бренд в список
          if (!matchedBrand && apiBrand) {
            const newBrand = { id: Date.now(), name: apiBrand };
            setBrands(prev => [...prev, newBrand]);
          }
          
          const finalBrand = matchedBrand ? matchedBrand.name : apiBrand;
          
          setFormData(prev => ({
            ...prev,
            brand: finalBrand
          }));
        }
      } else {
        setValidationError("Не удалось найти товар по артикулу");
        setProductInfo(null);
      }

      setIsSearching(false);
    };

    const timeoutId = setTimeout(searchProduct, 800);

    return () => clearTimeout(timeoutId);
  }, [article]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDropdownChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productInfo) {
      setValidationError("Сначала найдите товар по артикулу");
      return;
    }

    if (
      !formData.brand ||
      !formData.usSize ||
      !formData.ukSize ||
      !formData.euSize ||
      !formData.price ||
      !formData.cost ||
      !formData.condition ||
      !formData.provider
    ) {
      setValidationError("Заполните все обязательные поля");
      return;
    }

    setIsAdding(true);
    setValidationError("");

    const result = await executeApiCall(
      () =>
        onAdd({
          article: article.trim(),
          model: productInfo.model,
          color: productInfo.color,
          photo_url: productInfo.photo_url,
          brand: formData.brand,
          us_size: parseFloat(formData.usSize),
          uk_size: parseFloat(formData.ukSize),
          eu_size: parseFloat(formData.euSize),
          price: parseFloat(formData.price),
          purchase_price: parseFloat(formData.cost),
          condition: formData.condition,
          provider: formData.provider,
          city: formData.city,
          place: formData.place,
          fitting: formData.fitting,
          purchase_date: formData.purchaseDate,
        }),
      {
        successMessage: "Товар успешно добавлен",
        errorMessage: "Ошибка при добавлении товара",
      }
    );

    if (result !== null) {
      // Сброс формы при успехе
      setArticle("");
      setProductInfo(null);
      setFormData({
        brand: brands[0]?.name || "",
        usSize: "",
        ukSize: "",
        euSize: "",
        price: "",
        cost: "",
        condition: conditions[0]?.name || "",
        provider: "",
        city: cities[0] || "",
        place: "",
        fitting: false,
        purchaseDate: new Date().toISOString().split("T")[0],
      });

      onClose();
    }

    setIsAdding(false);
  };

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose} data-modal="add-product">
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Добавить товар</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <SearchSection data-tutorial="product-search">
          <SearchContainer>
            <Input
              label="Артикул"
              value={article}
              onChange={(e) => setArticle(e.target.value)}
              placeholder="Введите артикул товара"
              disabled={isSearching}
            />
          </SearchContainer>
        </SearchSection>

        {validationError && <ErrorMessage>{validationError}</ErrorMessage>}

        <ContentContainer>
          <FormSection>
            <form onSubmit={handleSubmit}>
              <FormGrid>
                <FormField>
                  <CustomDropdown
                    label="Бренд"
                    options={brands.map((brand) => ({
                      value: brand.name,
                      label: brand.name,
                    }))}
                    value={formData.brand}
                    onChange={(value) => handleDropdownChange("brand", value)}
                    placeholder="Выберите бренд"
                    required
                  />
                </FormField>

                <FormField data-tutorial="size-input">
                  <CustomDropdown
                    label="Размер US"
                    options={US_SIZE_OPTIONS}
                    value={formData.usSize}
                    onChange={(value) => handleDropdownChange("usSize", value)}
                    placeholder="Выберите размер"
                    required
                  />
                </FormField>

                <FormField data-tutorial="size-uk-input">
                  <CustomDropdown
                    label="Размер UK"
                    options={UK_SIZE_OPTIONS}
                    value={formData.ukSize}
                    onChange={(value) => handleDropdownChange("ukSize", value)}
                    placeholder="Выберите размер"
                    required
                  />
                </FormField>

                <FormField data-tutorial="size-eu-input">
                  <CustomDropdown
                    label="Размер EU"
                    options={EU_SIZE_OPTIONS}
                    value={formData.euSize}
                    onChange={(value) => handleDropdownChange("euSize", value)}
                    placeholder="Выберите размер"
                    required
                  />
                </FormField>

                <FormField data-tutorial="selling-price">
                  <Input
                    label="Цена продажи *"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="25000"
                    required
                  />
                </FormField>

                <FormField data-tutorial="purchase-price">
                  <Input
                    label="Цена покупки *"
                    name="cost"
                    type="number"
                    value={formData.cost}
                    onChange={handleInputChange}
                    placeholder="20000"
                    required
                  />
                </FormField>

                <FormField data-tutorial="condition-select">
                  <CustomDropdown
                    label="Состояние"
                    options={conditions.map((condition) => ({
                      value: condition.name,
                      label: condition.name,
                    }))}
                    value={formData.condition}
                    onChange={(value) =>
                      handleDropdownChange("condition", value)
                    }
                    placeholder="Выберите состояние"
                    required
                  />
                </FormField>

                <FormField data-tutorial="provider-input">
                  <ProviderSelector
                    label="Поставщик"
                    providers={providers}
                    value={formData.provider}
                    onChange={(value) =>
                      handleDropdownChange("provider", value)
                    }
                    placeholder="Выберите или введите поставщика"
                    required
                  />
                </FormField>

                <FormField>
                  <CustomDropdown
                    label="Город"
                    options={cities.map((city) => ({
                      value: city,
                      label: city,
                    }))}
                    value={formData.city}
                    onChange={(value) => handleDropdownChange("city", value)}
                    placeholder="Выберите город"
                  />
                </FormField>

                <FormField>
                  <CustomDropdown
                    label="Место встречи (станция метро)"
                  options={metroStations.map((station) => ({
                    value: station,
                    label: station,
                  }))}
                    value={formData.place}
                    onChange={(value) => handleDropdownChange("place", value)}
                    placeholder="Выберите станцию метро"
                  />
                </FormField>

                <FormField>
                  <Input
                    label="Дата покупки"
                    name="purchaseDate"
                    type="date"
                    value={formData.purchaseDate}
                    onChange={handleInputChange}
                  />
                </FormField>

                <CheckboxField>
                  <CheckboxWrapper>
                    <Checkbox
                      type="checkbox"
                      name="fitting"
                      checked={formData.fitting}
                      onChange={handleInputChange}
                      id="fitting-checkbox"
                    />
                    <CheckboxCustom
                      $checked={formData.fitting}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          fitting: !prev.fitting,
                        }))
                      }
                    />
                  </CheckboxWrapper>
                  <CheckboxLabel htmlFor="fitting-checkbox">
                    Есть примерка
                  </CheckboxLabel>
                </CheckboxField>
              </FormGrid>

              <ButtonsContainer>
                <Button onClick={onClose} variant="secondary">
                  Отмена
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!productInfo || isAdding}
                  data-tutorial="save-product-button"
                >
                  Добавить
                </Button>
              </ButtonsContainer>
            </form>
          </FormSection>

          <PreviewSection>
            {productInfo && (
              <ProductPreview>
                <ProductImage
                  src={productInfo.photo_url}
                  alt={productInfo.model}
                />
                <ProductInfo>
                  <ProductName>{productInfo.model}</ProductName>
                  <ProductColor>{productInfo.color}</ProductColor>
                </ProductInfo>
              </ProductPreview>
            )}
          </PreviewSection>
        </ContentContainer>

        {(isLoadingData || isAdding || isSearching) && (
          <LoadingOverlay>
            <Loader
              text={
                isAdding
                  ? "Добавление товара..."
                  : isSearching
                  ? "Поиск товара..."
                  : "Загрузка данных..."
              }
            />
          </LoadingOverlay>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddProductModal;
